/**
 * Admin: GPS-trackere (Teltonika TMT250).
 *
 * Registrer enheter (IMEI), knytt dem til en båt, og se online-status, siste
 * posisjon og batteri. Selve datamottaket skjer på TCP-serveren; her forvalter
 * vi enhetene.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.ts';
import { TrackHistory } from '../../components/TrackHistory.tsx';

function fmtAgo(iso: string | null): string {
  if (!iso) return 'aldri sett';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'nå nettopp';
  if (min < 60) return `${min} min siden`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} t siden`;
  return `${Math.floor(h / 24)} d siden`;
}
function isOnline(iso: string | null): boolean {
  return !!iso && Date.now() - new Date(iso).getTime() < 3 * 60_000;
}

export function AdminTrackers() {
  const qc = useQueryClient();
  const trackers = useQuery({
    queryKey: ['trackers'],
    queryFn: () => api.listTrackers(),
    refetchInterval: 10_000,
  });
  const boats = useQuery({ queryKey: ['boats'], queryFn: () => api.listBoats() });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['trackers'] });
  const create = useMutation({ mutationFn: api.createTracker, onSuccess: invalidate });
  const update = useMutation({
    mutationFn: (v: { id: string; data: Parameters<typeof api.updateTracker>[1] }) =>
      api.updateTracker(v.id, v.data),
    onSuccess: invalidate,
  });
  const del = useMutation({ mutationFn: (id: string) => api.deleteTracker(id), onSuccess: invalidate });

  const [imei, setImei] = useState('');
  const [label, setLabel] = useState('');
  const [boatId, setBoatId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [openPoints, setOpenPoints] = useState<string | null>(null);

  const boatList = boats.data ?? [];

  function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    create.mutate(
      { imei: imei.trim(), label: label.trim() || null, boatId: boatId || null },
      {
        onSuccess: () => {
          setImei('');
          setLabel('');
          setBoatId('');
        },
        onError: (e) => setErr(e instanceof ApiError ? e.message : 'Kunne ikke registrere'),
      },
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold">GPS-trackere</h1>
        <p className="text-xs text-slate-400 mt-1">
          Maskinvare-trackere (Teltonika TMT250). Registrer IMEI og knytt til en båt — posisjoner
          skrives automatisk inn i båtens aktive regatta (live kart og reprise).
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 sm:items-end"
      >
        <div>
          <label className="block text-xs text-slate-400 mb-1">IMEI</label>
          <input
            value={imei}
            onChange={(e) => setImei(e.target.value.replace(/\D/g, ''))}
            placeholder="15 siffer"
            inputMode="numeric"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Etikett (valgfri)</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="f.eks. Tracker 1"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Båt</label>
          <select
            value={boatId}
            onChange={(e) => setBoatId(e.target.value)}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
          >
            <option value="">Ingen</option>
            {boatList.map((b) => (
              <option key={b.id} value={b.id}>
                {b.sailNumber} – {b.boatName}
              </option>
            ))}
          </select>
        </div>
        <button
          disabled={imei.trim().length < 10 || create.isPending}
          className="px-4 py-2 bg-sky-600 hover:bg-sky-500 rounded-lg text-sm font-semibold disabled:opacity-40"
        >
          Registrer
        </button>
      </form>
      {err && <p className="text-sm text-red-300">{err}</p>}

      {trackers.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : (trackers.data ?? []).length === 0 ? (
        <p className="text-slate-500 text-sm">Ingen trackere registrert ennå.</p>
      ) : (
        <ul className="space-y-2">
          {trackers.data!.map((t) => (
            <li key={t.id} className="bg-slate-900/50 border border-slate-800 rounded-xl p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                        isOnline(t.lastSeenAt) ? 'bg-emerald-400' : 'bg-slate-600'
                      }`}
                    />
                    <span className="font-mono text-sm">{t.imei}</span>
                    {t.label && <span className="text-xs text-slate-400">{t.label}</span>}
                    {!t.active && <span className="text-xs text-amber-400">(deaktivert)</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {isOnline(t.lastSeenAt) ? (
                      <span className="text-emerald-400 font-semibold">● Aktiv nå</span>
                    ) : t.lastSeenAt ? (
                      <span className="text-slate-400">○ Inaktiv · sist sett {fmtAgo(t.lastSeenAt)}</span>
                    ) : (
                      <span className="text-slate-500">○ Aldri vært tilkoblet</span>
                    )}
                    {t.lastBatteryMv != null && ` · 🔋 ${(t.lastBatteryMv / 1000).toFixed(2)} V`}
                    {t.lastSatellites != null && ` · ${t.lastSatellites} sat`}
                    {t.lastSpeedKn != null && ` · ${t.lastSpeedKn.toFixed(1)} kn`}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirm(`Slette tracker ${t.imei}?`)) del.mutate(t.id);
                  }}
                  className="text-xs text-red-300 hover:text-red-200 shrink-0"
                >
                  Slett
                </button>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <label className="text-xs text-slate-400">Båt:</label>
                <select
                  value={t.boatId ?? ''}
                  onChange={(e) => update.mutate({ id: t.id, data: { boatId: e.target.value || null } })}
                  className="px-2 py-1 bg-slate-950 border border-slate-700 rounded text-xs"
                >
                  <option value="">Ingen</option>
                  {boatList.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.sailNumber} – {b.boatName}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setOpenPoints(openPoints === t.id ? null : t.id)}
                  className="text-xs text-sky-400 hover:text-sky-300"
                >
                  {openPoints === t.id ? '▾ Skjul registreringer' : '▸ Registreringer'}
                </button>
                {t.lastLat != null && t.lastLon != null && (
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${t.lastLat}&mlon=${t.lastLon}#map=14/${t.lastLat}/${t.lastLon}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-sky-400 ml-auto"
                  >
                    📍 {t.lastLat.toFixed(4)}, {t.lastLon.toFixed(4)} ↗
                  </a>
                )}
              </div>
              {openPoints === t.id && <TrackHistory source="tracker" id={t.id} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

